<template>
  <div class="breakdown-legend__container">
    <span
      v-t="'LABEL'"
      class="m-r-5"
    />
    <span 
      v-for="(groupBy, index) in breakdown"
      :key="groupBy.fieldName"
      :class="{ root: index === 0 }"
      class="group-level"
    >
      <span
        :id="'level' + index"
        class="label label-default"
        @click="() => showPopover(index)"
      >
        <span v-tooltip.top.hover="{text: $t('UPDATE_LEVEL_TT'), customClass: 'group-level__tooltip'}">{{ groupBy.displayName }} </span>
        <i 
          class="fa fa-angle-down m-l-5"
          :class="{'m-r-3': index !== 0}"
        />
        <i
          v-if="index > 0"
          v-tooltip.top.hover="{text: $t('DELETE_LEVEL_TT'), customClass: 'group-level__tooltip'}"
          class="fa fa-times group-level__delete m-l-5"
          @click.stop="() => deleteLevel(index)"
        />
      </span>
      <popover
        v-model="popoverOpen[index]"
        :target="'#level' + index"
        trigger="manual"
        placement="bottom"
        custom-class="breakdown-legend__popover"
        @show="popoverVisible[index] = true"
        @hide="popoverVisible[index] = false"
      >
        <template slot="popover">
          <InsightsGroupByDropdown
            :available-group-bys="availableGroupBys"
            :index="index"
            :focus-dropdown="popoverVisible[index]"
            :tag-grouping-already-selected="tagGroupingAlreadySelected"
            class="insights-group-by-dropdown"
            @input="onGroupByChange"
            @blur="hidePopover"
          />
        </template>
      </popover>
      <span class="level-separator">></span>
    </span>
    <span class="group-level add-level">
      <span
        :id="'level' + numBreakdownLevels"
        ref="addLevel"
        v-tooltip.top.hover="$t('ADD_LEVEL_TT')"
        class="label label-default"
        @click="() => showPopover(numBreakdownLevels)"
      ><i class="fa fa-plus p-r-5" /> {{ $t('LEVEL') }}</span>
      <popover
        v-model="popoverOpen[numBreakdownLevels]"
        :target="$refs.addLevel"
        trigger="manual"
        placement="bottom"
        custom-class="breakdown-legend__popover"
        @show="popoverVisible[numBreakdownLevels] = true"
        @hide="popoverVisible[numBreakdownLevels] = false"
      >
        <template slot="popover">
          <InsightsGroupByDropdown
            :available-group-bys="availableGroupBys"
            :index="numBreakdownLevels"
            :focus-dropdown="popoverVisible[numBreakdownLevels]"
            :tag-grouping-already-selected="tagGroupingAlreadySelected"
            class="insights-group-by-dropdown"
            @input="onGroupByChange"
            @blur="hidePopover"
          />
        </template>
      </popover>
    </span>
  </div>
</template>

<script>
import Vue from 'vue';
import { differenceWith, some } from 'lodash';

import InsightsGroupByDropdown from '../../ReportingInsights/InsightsGroupByDropdown';
import sharedMessages from '@/core/locales';
import messages from './locales';

export default {
  name: 'BreakdownLegend',
  components: { InsightsGroupByDropdown },
  props: {
    breakdown: {
      type: Array,
      default: () => []
    },
    groupBys: {
      type: Object,
      default() {
        return {
          columns: {
            Level: [{
              field: 'campaignName',
              displayName: 'Campaign Insights',
              helperText: 'Data segmented by campaign'
            }]
          },
          tags: {
            address: ['zip code', 'city', 'state', 'country', 'pincde', 'street 1', 'street 2', 'landmark', 'nearby']
          }
        };
      }
    }
  },
  data(){
    return { 
      popoverOpen: [],
      popoverVisible: []
    };
  },
  computed: {
    numBreakdownLevels() {
      return this.breakdown.length;
    },
    tagGroupingAlreadySelected() {
      return this.breakdown.some(b => b?.id?.startsWith('tag:'));
    },
    availableGroupBys() {
      const availableGroupBys = {
        columns: {},
        tags: {}
      };

      for (const [key, value] of Object.entries(this.groupBys.columns)) {
        availableGroupBys.columns[key] = differenceWith(value, this.breakdown, (a, b) => a.field === b.field);
      }
      if(!this.tagGroupingAlreadySelected) {
        for (const [key, value] of Object.entries(this.groupBys.tags)) {
          availableGroupBys.tags[key] = differenceWith(value, this.breakdown, (a, b) => {
            return (`tag:${a.tagType}:${a.name}`) === b.field;
          });
        }
      }
      return availableGroupBys;
    }
  },
  methods: {
    onGroupByChange(groupBy, index) {
      this.$emit('onChange', groupBy, index);
      this.hidePopover(index);
    },
    deleteLevel(index) {
      this.$emit('onDelete', index);
    },
    showPopover(index) {
      Vue.set(this.popoverOpen, index, true);
    },
    hidePopover(index) {
      Vue.set(this.popoverOpen, index, false);
    }
  },
  i18n: {
    locale: 'en',
    messages,
    sharedMessages
  }
};
</script>

<style lang="scss">
.breakdown-legend__container {
  padding-left: 12px;
  margin-right: auto;
  
  .group-level {
    user-select: none;
    display: inline-block;
    margin-left: 8px;
    
    &.root, &.add-level, .label, .group-level__delete {
      cursor: pointer;
    }

    &.root{
      .level-separator {
        margin-left: 8px;
      }
      .label {
        padding-right: 8px;
      }
    }

    .label {
      font-size: 10px;
      line-height: 19px;
      padding: 7px 10px;
      font-weight: 400;
      opacity: 1;
      transition: opacity 0.35s ease-in-out;
      border-radius: initial;
      &:hover {
        opacity: .8;
      }
    }
    &:nth-child(2) .label, &:nth-child(9) .label {
      background-color: var(--component-table__row--group-level-0);
    }
    &:nth-child(3) .label, &:nth-child(10) .label {
      background-color: var(--component-table__row--group-level-1);
    }
    &:nth-child(4) .label, &:nth-child(11) .label {
      background-color: var(--component-table__row--group-level-2);
    }
    &:nth-child(5) .label, &:nth-child(12) .label {
      background-color: var(--component-table__row--group-level-3);
    }
    &:nth-child(6) .label, &:nth-child(13) .label {
      background-color: var(--component-table__row--group-level-4);
    }
    &:nth-child(7) .label, &:nth-child(14) .label {
      background-color: var(--component-table__row--group-level-5);
    }
    &:nth-child(8) .label, &:nth-child(15) .label {
      background-color: var(--component-table__row--group-level-6);
    }

    &.add-level{
      margin-left: 5px;
       .label {
        letter-spacing: 1.6px;
        background-color: var(--component-btn--primary-bg-border-color);
       }
    }

    .group-level__delete, .fa-plus {
      opacity: .75;
      transition: opacity 0.35s ease-in-out;
      &:hover {
        opacity: 1;
      }
    }

    .level-separator {
      margin-left: 5px;
    }
  }
}

.breakdown-legend__popover {
  background-color: var(--component-in-app-notifications__popover);
  &.bottom {
    @include setPopoverArrowColor('border-bottom-color', var(--component-in-app-notifications__popover));
  }

  &.right {
    @include setPopoverArrowColor('border-right-color', var(--component-in-app-notifications__popover));
  }
  
  .popover-content {
    padding: 1px;
  }
  .form-group {
    padding-right: 0;
    padding-left: 0;
  }
}


.tooltip.top {
  &.group-level__tooltip {
    margin-top: -8px;
  }
  & .tooltip-inner {
    max-width: 210px;
  }
} 
</style>